import { type KeyValueRepo } from '#packages/data-collection/repositories/kv-repo';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { isNil } from '#packages/data-collection/libs/lodash';

export type SettingsServiceOpts<TValue = unknown, TKey = string> = {
  repo: KeyValueRepo<TKey, TValue>;
  key: TKey;
} & LoggingContext;

export class SettingsService<TValue = unknown, TKey = string> {
  protected readonly opts: SettingsServiceOpts<TValue, TKey>;

  constructor(opts: SettingsServiceOpts<TValue, TKey>) {
    this.opts = opts;
  }

  async load(defaultValue: TValue | null = null): Promise<TValue | null> {
    const { repo, key, logger } = this.opts;

    logger?.debug({ key }, 'Getting setting');

    const value = await repo.get(key);

    logger?.debug(
      { key, found: !isNil(value), defaultValue },
      'Setting retrieved by key',
    );

    return value ?? defaultValue;
  }

  async save(value: TValue): Promise<void> {
    const { repo, key, logger } = this.opts;
    logger?.debug({ key, value }, 'Setting setting');

    await repo.set(key, value);
  }

  async delete(): Promise<void> {
    const { repo, key, logger } = this.opts;
    logger?.debug({ key }, 'Deleting setting');
    return repo.delete(key);
  }
}
